import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowLeft, Ticket, Wine, ShoppingBag, Heart, Map as MapIcon, Sparkles,
  CheckCircle2, MapPin, Crown, Music, Utensils, Palette, Star, ExternalLink, ChevronRight,
} from 'lucide-react';

const SECTION_TILES = [
  { id: 'scripts',     icon: Ticket,        label: 'Food & Tequila Scripts', color: 'pink' },
  { id: 'vip-bottle',  icon: Crown,         label: 'VIP Bottle Service',     color: 'amber' },
  { id: 'merch',       icon: ShoppingBag,   label: 'Official Festival Merch', color: 'orange' },
  { id: 'donate',      icon: Heart,         label: 'Support the Aloha Cancer Project', color: 'pink' },
  { id: 'directory',   icon: Wine,          label: 'Tequila Directory',      color: 'pink' },
  { id: 'map',         icon: MapIcon,       label: 'Festival Map',           color: 'orange' },
  { id: 'experience',  icon: Sparkles,      label: 'Festival Experience',    color: 'amber' },
];

const colorClass = {
  pink:   { bg: 'from-pink-500/15 to-pink-500/5',     border: 'border-pink-500/30',     iconBg: 'from-pink-500 to-orange-500',   shadow: 'shadow-pink-500/30',   accent: 'text-pink-300' },
  amber:  { bg: 'from-amber-500/15 to-amber-500/5',   border: 'border-amber-500/30',    iconBg: 'from-amber-400 to-yellow-500',  shadow: 'shadow-amber-500/30',  accent: 'text-amber-300' },
  orange: { bg: 'from-orange-500/15 to-orange-500/5', border: 'border-orange-500/30',  iconBg: 'from-orange-500 to-amber-500',  shadow: 'shadow-orange-500/30', accent: 'text-orange-300' },
};

const SECTION_SPONSORS = [
  // Headliners first
  'SpotOn', 'Suntory', 'Pernod Ricard', 'Brown-Forman', 'Campari USA', 'Proximo',
  'Tequila Ocho', 'G4 Tequila', '3 Tres Tequila', 'Real del Valle',
  'Chun, Cordasco & Associates', 'Aloha Alkaline', 'Island Structural Contracting',
  'Island Beverage', 'Twilight Audio', 'Centered', 'INU',
  'Taqueria La Marea', 'Shaka Mex', 'La Piña Cantina', 'Pitch Sports Bar Kakaʻako',
  '13 Eleven Events', "Rad's Cosmos", 'Haley von Katz', 'Miko Francom',
];

const Card = ({ id, icon: Icon, title, color = 'pink', children, badge }) => {
  const c = colorClass[color] || colorClass.pink;
  return (
    <section
      id={id}
      data-testid={`welcome-section-${id}`}
      className={`bg-gradient-to-br ${c.bg} backdrop-blur-sm border ${c.border} rounded-3xl p-5 sm:p-7 scroll-mt-24`}
    >
      <div className="flex items-start gap-3 mb-4">
        <div className={`w-11 h-11 sm:w-12 sm:h-12 bg-gradient-to-br ${c.iconBg} rounded-2xl flex items-center justify-center shrink-0 shadow-lg ${c.shadow}`}>
          <Icon className={`w-5 h-5 sm:w-6 sm:h-6 ${color === 'amber' ? 'text-black' : 'text-white'}`} />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-xl sm:text-2xl font-black text-white leading-tight">{title}</h2>
          {badge && <div className={`text-xs font-bold uppercase tracking-wider ${c.accent} mt-1`}>{badge}</div>}
        </div>
      </div>
      <div className="text-white/80 text-sm sm:text-base leading-relaxed space-y-3">{children}</div>
    </section>
  );
};

const WelcomePage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1a0a14] via-[#0a0a0a] to-black text-white" data-testid="welcome-page">
      <Helmet>
        <title>Welcome | Aloha Tequila & Arts Festival 2026</title>
        <meta name="description" content="Guest information and festival guide for the Aloha Tequila & Arts Festival on June 20, 2026 — Waikiki. Scripts, VIP bottle service, merch, festival map, and more." />
      </Helmet>

      {/* Hero */}
      <div className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 bg-gradient-to-br from-pink-500/15 via-orange-500/5 to-amber-500/15 pointer-events-none" />
        <div className="absolute inset-0 opacity-30 pointer-events-none" style={{
          backgroundImage: 'radial-gradient(circle at 20% 20%, rgba(236,72,153,0.15) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(251,191,36,0.15) 0%, transparent 50%)',
        }} />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-10 sm:pt-12 sm:pb-14 text-center sm:text-left">
          <Link
            to="/"
            data-testid="welcome-back-home"
            className="inline-flex items-center gap-2 text-white/60 hover:text-pink-400 text-sm font-medium mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to homepage
          </Link>

          <div className="text-5xl mb-4">🌺</div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-tight mb-3">
            <span className="bg-gradient-to-r from-pink-400 via-orange-400 to-amber-400 bg-clip-text text-transparent">
              Aloha Tequila Fest
            </span>
          </h1>
          <p className="text-base sm:text-lg text-pink-300 font-bold mb-5">
            Guest Information &amp; Festival Guide
          </p>

          <p className="text-base sm:text-lg text-white/80 max-w-2xl leading-relaxed">
            <strong className="text-white">Welcome to Aloha Tequila Fest!</strong> Mahalo for joining us for an unforgettable evening of
            premium tequila, food, music, art, and community — all supporting the{' '}
            <strong className="text-pink-300">Aloha Cancer Project</strong>.
          </p>
        </div>
      </div>

      {/* Quick Jump */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 -mb-2">
        <div className="text-xs font-bold uppercase tracking-wider text-white/40 mb-3">In this guide</div>
        <div className="flex flex-wrap gap-2 mb-6">
          {SECTION_TILES.map((t) => (
            <a
              key={t.id}
              href={`#${t.id}`}
              data-testid={`welcome-jump-${t.id}`}
              className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-semibold bg-white/5 hover:bg-white/10 border border-white/10 hover:border-pink-500/40 text-white/75 hover:text-white px-3 py-1.5 rounded-full transition-all"
            >
              <t.icon className="w-3.5 h-3.5" />
              {t.label}
              <ChevronRight className="w-3 h-3 opacity-50" />
            </a>
          ))}
        </div>
      </div>

      {/* Sections */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 space-y-5 sm:space-y-6">

        {/* Scripts */}
        <Card id="scripts" icon={Ticket} title="Food & Tequila Scripts" color="pink" badge="Need More?">
          <p>Additional Food &amp; Tequila Scripts are available:</p>
          <ul className="space-y-2 pl-1">
            <li className="flex items-start gap-2"><CheckCircle2 className="w-5 h-5 text-pink-400 shrink-0 mt-0.5" /><span>At <strong className="text-white">Check-In</strong></span></li>
            <li className="flex items-start gap-2"><CheckCircle2 className="w-5 h-5 text-pink-400 shrink-0 mt-0.5" /><span>At the <strong className="text-white">Main Bars on the 1st and 3rd Level</strong></span></li>
          </ul>
          <p className="bg-white/[0.06] border border-white/10 rounded-xl p-3 text-white/85">
            You can also <strong className="text-pink-300">place an order for any of the tequilas you like</strong> at the Main Bars on the first and 3rd level.
          </p>
        </Card>

        {/* VIP Bottle Service */}
        <Card id="vip-bottle" icon={Crown} title="VIP Bottle Service" color="amber" badge="Limited Availability">
          <p>Upgrade your festival experience to <strong className="text-amber-300">VIP Bottle Service</strong>.</p>
          <div className="bg-gradient-to-br from-amber-500/20 to-yellow-500/10 border border-amber-500/40 rounded-2xl p-4 sm:p-5 mt-2">
            <div className="text-amber-300 text-xs font-black uppercase tracking-wider mb-1">VIP Table Package</div>
            <div className="text-white text-3xl sm:text-4xl font-black tabular-nums mb-3">$1,500</div>
            <ul className="space-y-1.5 text-sm text-white/85">
              <li className="flex items-center gap-2"><Star className="w-4 h-4 text-amber-400 shrink-0" /> Two Premium Tequila Bottles</li>
              <li className="flex items-center gap-2"><Star className="w-4 h-4 text-amber-400 shrink-0" /> VIP Admission for 8 Guests</li>
              <li className="flex items-center gap-2"><Star className="w-4 h-4 text-amber-400 shrink-0" /> Reserved VIP Seating</li>
              <li className="flex items-center gap-2"><Star className="w-4 h-4 text-amber-400 shrink-0" /> Exclusive VIP Access</li>
              <li className="flex items-center gap-2"><Star className="w-4 h-4 text-amber-400 shrink-0" /> Premium Festival Experience</li>
            </ul>
          </div>
          <p className="text-white/65 text-sm">Visit <strong className="text-amber-300">Guest Services</strong> for details.</p>
        </Card>

        {/* Merch */}
        <Card id="merch" icon={ShoppingBag} title="Official Festival Merch" color="orange" badge="At the Merch Booth">
          <p>Located <strong className="text-white">immediately to the left when entering</strong>.</p>
          <div>
            <div className="text-white/60 text-xs font-bold uppercase tracking-wider mb-2">Available Merchandise</div>
            <ul className="space-y-1.5 text-sm">
              <li className="flex items-start gap-2 text-white/85"><span className="text-orange-400">•</span> Aloha Cancer Project Shirts</li>
              <li className="flex items-start gap-2 text-white/85"><span className="text-orange-400">•</span> Official Aloha Tequila Fest Shirts</li>
              <li className="flex items-start gap-2 text-white/85"><span className="text-orange-400">•</span> Limited Edition Festival Merchandise</li>
            </ul>
          </div>
          <a href="https://www.alohatequilafest.com" target="_blank" rel="noopener noreferrer" data-testid="welcome-merch-online" className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white font-bold text-sm px-5 py-2.5 rounded-full transition-all hover:scale-105">
            Shop Online <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </Card>

        {/* Donate */}
        <Card id="donate" icon={Heart} title="Support the Aloha Cancer Project" color="pink">
          <p>Every dollar helps <strong className="text-white">local cancer patients and families throughout Hawai‘i</strong>.</p>
          <div>
            <div className="text-white/60 text-xs font-bold uppercase tracking-wider mb-2">Additional Donations May Be Made At</div>
            <ul className="space-y-1.5 text-sm">
              <li className="flex items-start gap-2"><MapPin className="w-4 h-4 text-pink-400 shrink-0 mt-0.5" /><span>Festival Check-In</span></li>
              <li className="flex items-start gap-2"><MapPin className="w-4 h-4 text-pink-400 shrink-0 mt-0.5" /><span>Merchandise Booth</span></li>
              <li className="flex items-start gap-2"><MapPin className="w-4 h-4 text-pink-400 shrink-0 mt-0.5" /><span>Online</span></li>
            </ul>
          </div>
          <p className="text-white/70 italic">Thank you for your generosity and support.</p>
        </Card>

        {/* Directory */}
        <Card id="directory" icon={Wine} title="Tequila Directory" color="pink" badge="Find Every Brand & Booth">
          <Link
            to="/tequila"
            data-testid="welcome-directory-link"
            className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/15 hover:border-pink-500/40 rounded-full px-4 py-2 text-sm font-semibold text-pink-300 transition-colors"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            alohatequilafest.com/tequila
          </Link>
          <ul className="grid sm:grid-cols-2 gap-y-1.5 text-sm mt-2">
            {['Participating Tequila Brands','Booth Numbers','Product Information','Distillery Information','Tasting Notes'].map((x) => (
              <li key={x} className="flex items-center gap-2 text-white/85"><CheckCircle2 className="w-4 h-4 text-pink-400 shrink-0" /> {x}</li>
            ))}
          </ul>
        </Card>

        {/* Map */}
        <Card id="map" icon={MapIcon} title="Festival Map" color="orange" badge="Navigate the Festival">
          <Link
            to="/map"
            data-testid="welcome-map-link"
            className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/15 hover:border-orange-500/40 rounded-full px-4 py-2 text-sm font-semibold text-orange-300 transition-colors"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            alohatequilafest.com/map
          </Link>
          <ul className="grid sm:grid-cols-2 gap-y-1.5 text-sm mt-2">
            {['Tequila Booths','Food Vendors','VIP Lounge','Artist Market','Restrooms','Entertainment Areas','Emergency Exits'].map((x) => (
              <li key={x} className="flex items-center gap-2 text-white/85"><CheckCircle2 className="w-4 h-4 text-orange-400 shrink-0" /> {x}</li>
            ))}
          </ul>
        </Card>

        {/* Experience */}
        <Card id="experience" icon={Sparkles} title="Festival Experience" color="amber" badge="Enjoy">
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              { icon: Wine,      text: '113+ Premium Tequilas',  sub: '29 in VIP Section' },
              { icon: Utensils,  text: 'Amazing Food Vendors',   sub: 'Shaka Mex · La Piña · La Marea + more' },
              { icon: Music,     text: 'Live DJs & Entertainment', sub: 'All night long' },
              { icon: Palette,   text: 'Local Artists & Vendors', sub: 'Artist Market on 3rd Floor' },
              { icon: Crown,     text: 'VIP Lounge Experience',  sub: "Moani's · 3rd Floor" },
              { icon: Heart,     text: 'Support Aloha Cancer Project', sub: 'Every dollar matters' },
            ].map((row) => (
              <div key={row.text} className="flex items-start gap-2.5 bg-white/[0.04] border border-white/10 rounded-xl p-3">
                <row.icon className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                <div>
                  <div className="text-sm font-bold text-white leading-tight">{row.text}</div>
                  <div className="text-xs text-white/55 mt-0.5">{row.sub}</div>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Sponsors */}
        <section className="bg-gradient-to-br from-pink-500/10 via-orange-500/5 to-amber-500/10 border border-white/10 rounded-3xl p-5 sm:p-7 text-center" data-testid="welcome-sponsors">
          <div className="text-xs font-black uppercase tracking-[0.2em] text-pink-300 mb-2">Mahalo</div>
          <h2 className="text-2xl sm:text-3xl font-black text-white mb-5">Thank You to Our Sponsors</h2>
          <div className="flex flex-wrap justify-center gap-1.5 max-w-2xl mx-auto">
            {SECTION_SPONSORS.map((s) => (
              <span key={s} className="text-xs sm:text-sm font-semibold bg-white/5 border border-white/15 text-white/80 px-3 py-1.5 rounded-full">
                {s}
              </span>
            ))}
          </div>
          <p className="text-white/45 text-xs mt-5">+ a special mahalo to <strong className="text-pink-300">Tamura&apos;s</strong> and <strong className="text-pink-300">Fujioka&apos;s</strong> for the bottle pickup partnership.</p>
        </section>

        {/* Footer */}
        <p className="text-center text-xs text-white/40 leading-relaxed pt-2">
          🌺 Aloha Tequila Fest 2026 · International Market Place, Waikiki · 21+ event · Drink responsibly
        </p>
      </div>
    </div>
  );
};

export default WelcomePage;
